import { useEffect, useState } from "react";
import {
  Check,
  CloudOff,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
  X,
} from "lucide-react";
import { AdminPage } from "@/components/AdminPage";
import { useToast } from "@/components/Toast";
import { ApiClientError } from "@podecontratar/shared/contract/client-error";
import { api } from "@/lib/api";
import { mapAdminApiError } from "@/lib/errors";
import type { ApiContract } from "@podecontratar/shared/contract/index";
import type { EndpointOutput } from "@podecontratar/shared/contract/endpoint";

type Coupon = EndpointOutput<ApiContract["GET /admin/coupons"]>[number];

/** Page size used while paging through the full coupon list (see `load`). */
const COUPONS_PAGE_SIZE = 100;

const APPLY_LABEL: Record<Coupon["apply_to"], string> = {
  pf: "Apenas PF",
  pj: "Apenas PJ",
  any: "PF e PJ",
};

function formatDiscount(c: Coupon): string {
  return c.discount_type === "percent"
    ? `${c.discount_value}%`
    : `R$ ${(c.discount_value / 100).toFixed(2).replace(".", ",")}`;
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso.replace(" ", "T") + (iso.includes("T") ? "" : "Z"))
    .toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
}

export default function AdminCouponsPage() {
  const toast = useToast();
  const [coupons, setCoupons] = useState<Coupon[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Coupon | "new" | null>(null);
  /** Per-row sync flag so the icon spins only on the row being mirrored. */
  const [syncing, setSyncing] = useState<Set<string>>(new Set());
  /** Header-level sync flag for the bulk button. */
  const [syncingAll, setSyncingAll] = useState(false);

  /**
   * The management page wants the full set (for the "sync all" bulk action
   * and a stable active-first display), so this pages through every page of
   * `GET /admin/coupons` and concatenates — the list is small (site coupons,
   * not referral codes) so this stays cheap. Keyset pagination dropped the
   * old `active DESC` primary sort (incompatible with a stable keyset
   * order); it's restored here, client-side, once every page has loaded.
   */
  async function load() {
    setCoupons(null);
    setError(null);
    try {
      const all: Coupon[] = [];
      let cursor: string | undefined;
      for (;;) {
        const page = await api.request("GET /admin/coupons", { query: { limit: COUPONS_PAGE_SIZE, cursor } });
        all.push(...page.items);
        if (!page.next_cursor) break;
        cursor = page.next_cursor;
      }
      all.sort((a, b) => {
        if (a.active !== b.active) return a.active ? -1 : 1;
        return a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : 0;
      });
      setCoupons(all);
    } catch (err) {
      setError(mapAdminApiError(err));
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function handleDelete(code: string) {
    if (!confirm(`Apagar cupom ${code}?`)) return;
    try {
      await api.request("DELETE /admin/coupons/:code", { params: { code } });
    } catch {
      // Best-effort delete — same behavior as before (no error surfaced).
    }
    load();
  }

  /**
   * Calls POST /admin/coupons/:code/sync, which (re-)mirrors the row to
   * Stripe (Coupon + Promotion Code) and persists the resulting ids. Returns
   * true on success so the bulk path can count failures separately.
   */
  async function syncOne(code: string): Promise<boolean> {
    setSyncing((prev) => new Set(prev).add(code));
    try {
      await api.request("POST /admin/coupons/:code/sync", { params: { code } });
      return true;
    } catch {
      return false;
    } finally {
      setSyncing((prev) => {
        const next = new Set(prev);
        next.delete(code);
        return next;
      });
    }
  }

  async function handleSyncOne(code: string) {
    const ok = await syncOne(code);
    if (ok) {
      toast.push(`${code} sincronizado com Stripe.`, "success");
      load();
    } else {
      toast.push(`Falha ao sincronizar ${code}.`, "error");
    }
  }

  async function handleSyncAll() {
    if (!coupons || syncingAll) return;
    setSyncingAll(true);
    try {
      // Run in series. Stripe rate-limits + we want predictable error
      // reporting (which code failed).
      let okCount = 0;
      let failCount = 0;
      for (const c of coupons) {
        // eslint-disable-next-line no-await-in-loop
        const ok = await syncOne(c.code);
        if (ok) okCount += 1;
        else failCount += 1;
      }
      if (failCount === 0) {
        toast.push(`${okCount} cupom(ns) sincronizados.`, "success");
      } else {
        toast.push(
          `${okCount} sincronizados, ${failCount} falharam.`,
          failCount === coupons.length ? "error" : "info"
        );
      }
      load();
    } finally {
      setSyncingAll(false);
    }
  }

  return (
    <AdminPage
      eyebrow="Cupons"
      title="Cupons de desconto"
      description="Códigos aplicáveis no checkout de consulta. Códigos de indicação (PCXXXXXX) não vivem aqui — são gerados a partir do referral_code dos usuários."
      action={
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleSyncAll}
            disabled={syncingAll || coupons === null || coupons.length === 0}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface px-4 py-2 text-sm font-medium text-text transition-colors hover:bg-surface-alt disabled:opacity-50"
            title="Espelha todos os cupons no Stripe (Coupon + Promotion Code)"
          >
            {syncingAll ? (
              <Loader2 size={14} className="animate-spin" aria-hidden />
            ) : (
              <RefreshCw size={14} strokeWidth={2} aria-hidden />
            )}
            Sincronizar com Stripe
          </button>
          <button
            type="button"
            onClick={() => setEditing("new")}
            className="inline-flex items-center gap-1.5 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-text-inverse transition-colors hover:bg-accent-hover"
          >
            <Plus size={14} strokeWidth={2} aria-hidden />
            Novo cupom
          </button>
        </div>
      }
    >
      {error ? (
        <div className="rounded-3xl border border-danger/30 bg-danger/10 p-5 text-sm text-text">
          {error}
        </div>
      ) : coupons === null ? (
        <div className="grid place-items-center rounded-3xl border border-border bg-surface py-16">
          <Loader2 size={20} className="animate-spin text-text-tertiary" aria-hidden />
        </div>
      ) : coupons.length === 0 ? (
        <div className="rounded-3xl border border-border bg-surface p-12 text-center">
          <h2 className="text-base font-semibold tracking-tight text-text">
            Nenhum cupom cadastrado
          </h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-text-secondary">
            Crie o primeiro pra liberar descontos no checkout.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-3xl border border-border bg-surface">
          <table className="w-full table-auto">
            <thead>
              <tr className="border-b border-border bg-surface-alt text-[10px] font-medium uppercase tracking-[0.18em] text-text-tertiary">
                <th className="px-3 py-3 text-left sm:px-5">Código</th>
                <th className="hidden px-3 py-3 text-left lg:table-cell lg:px-5">Descrição</th>
                <th className="px-3 py-3 text-left sm:px-5">Desconto</th>
                <th className="hidden px-3 py-3 text-left md:table-cell md:px-5">Aplica em</th>
                <th className="hidden px-3 py-3 text-left lg:table-cell lg:px-5">Validade</th>
                <th className="hidden px-3 py-3 text-left md:table-cell md:px-5">Uso</th>
                <th className="hidden px-3 py-3 text-left xl:table-cell xl:px-5">Criado por</th>
                <th className="px-3 py-3 text-left sm:px-5">Status</th>
                <th className="px-3 py-3 text-right sm:px-5">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border text-sm">
              {coupons.map((c) => (
                <tr key={c.code} className="hover:bg-surface-alt">
                  <td className="px-3 py-3 sm:px-5">
                    <code className="font-medium text-text">{c.code}</code>
                  </td>
                  <td className="hidden truncate px-3 py-3 text-text-secondary lg:table-cell lg:px-5">{c.label}</td>
                  <td className="px-3 py-3 text-text sm:px-5">{formatDiscount(c)}</td>
                  <td className="hidden px-3 py-3 text-text-secondary md:table-cell md:px-5">{APPLY_LABEL[c.apply_to]}</td>
                  <td className="hidden px-3 py-3 text-[11px] text-text-tertiary lg:table-cell lg:px-5">{formatDate(c.expires_at)}</td>
                  <td className="hidden px-3 py-3 text-text-secondary md:table-cell md:px-5">
                    {c.used_count}
                    {c.max_uses !== null && (
                      <span className="text-text-tertiary"> / {c.max_uses}</span>
                    )}
                    <p className="text-[11px] text-text-tertiary">
                      {c.max_uses_per_user === null
                        ? "Ilimitado por usuário"
                        : `${c.max_uses_per_user}× por usuário`}
                    </p>
                  </td>
                  <td className="hidden truncate px-3 py-3 text-[11px] text-text-tertiary xl:table-cell xl:px-5">
                    {c.created_by_email ?? "—"}
                  </td>
                  <td className="px-3 py-3 sm:px-5">
                    <span
                      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] ${
                        c.active
                          ? "bg-accent-soft text-accent border-accent/30"
                          : "bg-surface-alt text-text-tertiary border-border"
                      }`}
                    >
                      {c.active ? "Ativo" : "Inativo"}
                    </span>
                    {!c.active && c.expired_at && (
                      <p className="mt-1 text-[11px] text-text-tertiary">
                        Expirado em {formatDate(c.expired_at)}
                      </p>
                    )}
                  </td>
                  <td className="px-3 py-3 text-right sm:px-5">
                    <div className="inline-flex items-center gap-1">
                      <StripeSyncButton
                        coupon={c}
                        syncing={syncing.has(c.code)}
                        onClick={() => handleSyncOne(c.code)}
                      />
                      <button
                        type="button"
                        onClick={() => setEditing(c)}
                        className="rounded-md p-1.5 text-text-tertiary transition-colors hover:bg-surface-alt hover:text-text"
                        title="Editar"
                      >
                        <Pencil size={13} strokeWidth={1.75} aria-hidden />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(c.code)}
                        className="rounded-md p-1.5 text-text-tertiary transition-colors hover:bg-surface-alt hover:text-danger"
                        title="Apagar"
                      >
                        <Trash2 size={13} strokeWidth={1.75} aria-hidden />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <CouponDialog
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </AdminPage>
  );
}

interface DialogProps {
  initial: Coupon | null;
  onClose: () => void;
  onSaved: () => void;
}

function CouponDialog({ initial, onClose, onSaved }: DialogProps) {
  const isEdit = initial !== null;
  const [code, setCode] = useState(initial?.code ?? "");
  const [label, setLabel] = useState(initial?.label ?? "");
  const [discountType, setDiscountType] = useState<"percent" | "amount">(
    initial?.discount_type ?? "percent"
  );
  const [discountValue, setDiscountValue] = useState<string>(
    initial ? String(initial.discount_value) : ""
  );
  const [applyTo, setApplyTo] = useState<Coupon["apply_to"]>(initial?.apply_to ?? "any");
  const [expiresAt, setExpiresAt] = useState<string>(
    initial?.expires_at ? initial.expires_at.slice(0, 10) : ""
  );
  const [maxUses, setMaxUses] = useState<string>(
    initial?.max_uses != null ? String(initial.max_uses) : ""
  );
  const [maxUsesPerUser, setMaxUsesPerUser] = useState<string>(
    initial?.max_uses_per_user != null ? String(initial.max_uses_per_user) : ""
  );
  const [active, setActive] = useState(initial ? initial.active : true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const expires_at = expiresAt ? new Date(expiresAt + "T23:59:59Z").toISOString() : null;
      const max_uses = maxUses ? parseInt(maxUses, 10) : null;
      const max_uses_per_user = maxUsesPerUser ? parseInt(maxUsesPerUser, 10) : null;
      if (isEdit) {
        await api.request("PATCH /admin/coupons/:code", {
          params: { code: initial.code },
          body: {
            label: label.trim(),
            discount_type: discountType,
            discount_value: parseInt(discountValue, 10),
            apply_to: applyTo,
            expires_at,
            max_uses,
            max_uses_per_user,
            active,
          },
        });
      } else {
        await api.request("POST /admin/coupons", {
          body: {
            code: code.trim().toUpperCase(),
            label: label.trim(),
            discount_type: discountType,
            discount_value: parseInt(discountValue, 10),
            apply_to: applyTo,
            expires_at,
            max_uses,
            max_uses_per_user,
            active,
          },
        });
      }
      onSaved();
    } catch (err) {
      setError(
        err instanceof ApiClientError && err.code === "already_exists"
          ? "Já existe um cupom com esse código."
          : "Não foi possível salvar."
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-text/60 p-4 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-lg overflow-hidden rounded-3xl border border-border bg-surface shadow-[var(--shadow-card-lift)]"
      >
        <header className="flex items-center justify-between border-b border-border px-7 py-5">
          <h2 className="text-base font-semibold tracking-tight text-text">
            {isEdit ? `Editar ${initial.code}` : "Novo cupom"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-md p-1.5 text-text-tertiary transition-colors hover:bg-surface-alt hover:text-text"
            aria-label="Fechar"
          >
            <X size={16} aria-hidden />
          </button>
        </header>
        <div className="grid gap-4 px-7 py-6 sm:grid-cols-2">
          <Field label="Código">
            <input
              required
              disabled={isEdit}
              value={code}
              onChange={(e) =>
                setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ""))
              }
              placeholder="BEMVINDO10"
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text outline-none focus:border-accent disabled:opacity-50"
            />
          </Field>
          <Field label="Descrição">
            <input
              required
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="10% off na primeira consulta"
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text outline-none focus:border-accent"
            />
          </Field>
          <Field label="Tipo">
            <select
              value={discountType}
              onChange={(e) => setDiscountType(e.target.value as "percent" | "amount")}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text outline-none focus:border-accent"
            >
              <option value="percent">Percentual (%)</option>
              <option value="amount">Valor fixo (centavos)</option>
            </select>
          </Field>
          <Field label={discountType === "percent" ? "Valor (%)" : "Valor (cents)"}>
            <input
              required
              type="number"
              min={1}
              value={discountValue}
              onChange={(e) => setDiscountValue(e.target.value)}
              placeholder={discountType === "percent" ? "10" : "500"}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text outline-none focus:border-accent"
            />
          </Field>
          <Field label="Aplica em">
            <select
              value={applyTo}
              onChange={(e) => setApplyTo(e.target.value as Coupon["apply_to"])}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text outline-none focus:border-accent"
            >
              <option value="any">PF e PJ</option>
              <option value="pf">Apenas PF</option>
              <option value="pj">Apenas PJ</option>
            </select>
          </Field>
          <Field label="Validade (opcional)">
            <input
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text outline-none focus:border-accent"
            />
          </Field>
          <Field label="Usos máximos (opcional)">
            <input
              type="number"
              min={1}
              value={maxUses}
              onChange={(e) => setMaxUses(e.target.value)}
              placeholder="Sem limite"
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text outline-none focus:border-accent"
            />
          </Field>
          <Field label="Usos por usuário (opcional)">
            <input
              type="number"
              min={1}
              value={maxUsesPerUser}
              onChange={(e) => setMaxUsesPerUser(e.target.value)}
              placeholder="Ilimitado"
              aria-describedby="coupon-per-user-hint"
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text outline-none focus:border-accent"
            />
            <p id="coupon-per-user-hint" className="mt-1 text-[11px] text-text-tertiary">
              Conta compras pagas. Reembolso ou pagamento abandonado não conta.
            </p>
          </Field>
          <Field label="Status">
            {/* Match only the height of the sibling <input>/<select> so the
                row sits on the same baseline; no border / no left padding —
                the checkbox lines up with the inputs' left edge naturally. */}
            <label className="inline-flex h-[2.375rem] items-center gap-2 text-sm text-text">
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                className="h-4 w-4 accent-[color:var(--color-accent)]"
              />
              Ativo
            </label>
          </Field>
        </div>
        {error && (
          <div className="mx-7 mb-4 rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-text">
            {error}
          </div>
        )}
        <footer className="flex items-center justify-end gap-3 border-t border-border bg-surface-alt px-7 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg border border-border bg-surface px-4 py-2 text-sm font-medium text-text transition-colors hover:bg-surface-alt disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-text-inverse transition-colors hover:bg-accent-hover disabled:opacity-50"
          >
            {submitting && <Loader2 size={13} className="animate-spin" aria-hidden />}
            {isEdit ? "Salvar alterações" : "Criar cupom"}
          </button>
        </footer>
      </form>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[11px] font-medium uppercase tracking-[0.18em] text-text-tertiary">
        {label}
      </span>
      {children}
    </label>
  );
}

/**
 * Inline Stripe sync indicator + trigger.
 *   - synced  → small accent check, title shows the last-sync timestamp; click
 *               re-syncs to refresh after a Stripe-side edit.
 *   - pending → muted refresh icon, click pushes the row to Stripe.
 *   - busy    → spinner; button disabled.
 */
function StripeSyncButton({
  coupon,
  syncing,
  onClick,
}: {
  coupon: Coupon;
  syncing: boolean;
  onClick: () => void;
}) {
  const synced = Boolean(coupon.stripe_coupon_id);
  const title = synced
    ? `Sincronizado com Stripe${
        coupon.stripe_synced_at ? ` em ${formatDate(coupon.stripe_synced_at)}` : ""
      }. Clique para reenviar.`
    : "Sincronizar com Stripe";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={syncing}
      title={title}
      className={`rounded-md p-1.5 transition-colors disabled:opacity-50 ${
        synced
          ? "text-accent hover:bg-accent-soft"
          : "text-text-tertiary hover:bg-surface-alt hover:text-text"
      }`}
    >
      {syncing ? (
        <Loader2 size={13} className="animate-spin" aria-hidden />
      ) : synced ? (
        <Check size={13} strokeWidth={2} aria-hidden />
      ) : (
        <CloudOff size={13} strokeWidth={1.75} aria-hidden />
      )}
    </button>
  );
}
